import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acquireMaterial,
  acquireTool,
  listMaterialAcquisitions,
  listToolAcquisitions,
  unacquireMaterial,
  unacquireTool,
  updateMaterialPrice,
  updateToolPrice,
} from '@/api/acquisitions'
import { FOLLOWINGS_QUERY_KEY } from '@/features/doer/use-doer'
import type { MaterialAcquisition, ToolAcquisition } from '@/api/types'

/**
 * Phase 12: per-Following acquisition state. Two parallel queries (materials + tools) instead
 * of inlining into FollowingDetail — server-side inlining would have created a `doer →
 * acquisitions → doer` modulith cycle (acquisitions reads project_followings via
 * doer.api.FollowingLookup; the reverse direction would close the loop). Same shape Phase 10
 * settled on for favorite-stats enrichment.
 */
export const ACQUISITIONS_QUERY_KEY = ['acquisitions'] as const

export const useMaterialAcquisitions = (projectId: string | undefined) =>
  useQuery<MaterialAcquisition[]>({
    queryKey: [...ACQUISITIONS_QUERY_KEY, 'materials', projectId],
    queryFn: () => listMaterialAcquisitions(projectId as string),
    enabled: Boolean(projectId),
  })

export const useToolAcquisitions = (projectId: string | undefined) =>
  useQuery<ToolAcquisition[]>({
    queryKey: [...ACQUISITIONS_QUERY_KEY, 'tools', projectId],
    queryFn: () => listToolAcquisitions(projectId as string),
    enabled: Boolean(projectId),
  })

interface MaterialMutationVars {
  snapshotAttachmentId: string
  lockedUnitPriceCents?: number | null
}

const invalidateMaterials = (queryClient: ReturnType<typeof useQueryClient>, projectId: string) => {
  void queryClient.invalidateQueries({
    queryKey: [...ACQUISITIONS_QUERY_KEY, 'materials', projectId],
  })
  // Drift display + locked-price annotations live on the FollowingDetail render, but the
  // Following's own data isn't changing — only the acquisition list. Avoid invalidating
  // FOLLOWINGS_QUERY_KEY broadly so we don't refetch the snapshot tree on every checkbox tap.
  void queryClient.invalidateQueries({ queryKey: [...FOLLOWINGS_QUERY_KEY, 'detail', projectId] })
}

const invalidateTools = (queryClient: ReturnType<typeof useQueryClient>, projectId: string) => {
  void queryClient.invalidateQueries({
    queryKey: [...ACQUISITIONS_QUERY_KEY, 'tools', projectId],
  })
  void queryClient.invalidateQueries({ queryKey: [...FOLLOWINGS_QUERY_KEY, 'detail', projectId] })
}

export const useAcquireMaterial = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: MaterialMutationVars) => acquireMaterial(projectId, vars),
    onSettled: () => invalidateMaterials(queryClient, projectId),
  })
}

export const useUnacquireMaterial = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (snapshotAttachmentId: string) =>
      unacquireMaterial(projectId, snapshotAttachmentId),
    onSettled: () => invalidateMaterials(queryClient, projectId),
  })
}

interface UpdateMaterialPriceVars {
  snapshotAttachmentId: string
  lockedUnitPriceCents: number | null
}

export const useUpdateMaterialPrice = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ snapshotAttachmentId, lockedUnitPriceCents }: UpdateMaterialPriceVars) =>
      updateMaterialPrice(projectId, snapshotAttachmentId, lockedUnitPriceCents),
    onSettled: () => invalidateMaterials(queryClient, projectId),
  })
}

interface ToolMutationVars {
  catalogToolId: string
  lockedUnitPriceCents?: number | null
}

export const useAcquireTool = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (vars: ToolMutationVars) => acquireTool(projectId, vars),
    onSettled: () => invalidateTools(queryClient, projectId),
  })
}

export const useUnacquireTool = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (catalogToolId: string) => unacquireTool(projectId, catalogToolId),
    onSettled: () => invalidateTools(queryClient, projectId),
  })
}

interface UpdateToolPriceVars {
  catalogToolId: string
  lockedUnitPriceCents: number | null
}

export const useUpdateToolPrice = (projectId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ catalogToolId, lockedUnitPriceCents }: UpdateToolPriceVars) =>
      updateToolPrice(projectId, catalogToolId, lockedUnitPriceCents),
    onSettled: () => invalidateTools(queryClient, projectId),
  })
}
